import { weekDays, weekStartIso, type WeekGrid } from "../../data/schedule-builder";

export type CoverageView = "staff" | "site";
export type CoverageFilter = "all" | "open" | "at-risk";

/** Only non-identifying navigation context is allowed in the address. */
export function readCoverageNavigation(search: string, fallbackDate: string): { view: CoverageView; date: string } {
  const params = new URLSearchParams(search);
  const date = params.get("coverageDate");
  return {
    view: params.get("coverageView") === "site" ? "site" : "staff",
    date: isCoverageDate(date) ? date : fallbackDate,
  };
}

export function isCoverageDate(value: string | null): value is string {
  if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value) || value.startsWith("0000")) return false;
  const date = new Date(`${value}T12:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return false;
  const days = weekDays(weekStartIso(value));
  // The existing week model uses Date.UTC's year semantics and four-digit ISO labels.
  // Reject addresses that it cannot round-trip instead of querying a different century
  // or rendering an extended-year/duplicate calendar date.
  return days.includes(value) && days.every(day => /^\d{4}-\d{2}-\d{2}$/.test(day));
}

export function coverageNavigationSearch(search: string, view: CoverageView, date: string): string {
  const params = new URLSearchParams(search);
  params.set("coverageView", view);
  if (isCoverageDate(date)) params.set("coverageDate", date);
  return params.toString();
}

/** Derive only from the already loaded week. Slot tones retain the existing model's rules. */
export function filterCoverageGrid(grid: WeekGrid, site: string, search: string, filter: CoverageFilter): WeekGrid {
  const query = search.trim().toLocaleLowerCase();
  const posts = grid.posts.flatMap(post => {
    if (site !== "" && post.siteName !== site) return [];
    const matchesPost = `${post.siteName} ${post.routeName}`.toLocaleLowerCase().includes(query);
    const days = post.days.map(day => ({ ...day, slots: day.slots.filter(slot =>
      (filter === "all" || slot.tone === filter) &&
      (matchesPost || slot.credentials.some(credential => credential.replaceAll("_", " ").toLocaleLowerCase().includes(query)))
    ) }));
    return days.some(day => day.slots.length > 0) ? [{ ...post, days }] : [];
  });
  const slots = posts.flatMap(post => post.days.flatMap(day => day.slots));
  return { ...grid, posts, summary: {
    totalSlots: slots.length,
    openSlots: slots.filter(slot => slot.tone === "open").length,
    atRiskSlots: slots.filter(slot => slot.tone === "at-risk").length,
  } };
}
